import { useState, forwardRef, useCallback, useMemo } from "react";
import Modal, { CommonModalProps } from "..";
import { DbShareMediaInfos } from "../../../Core/Api/Types/db-share-media-infos";
import FormSection from "../../Forms/FormSection/form-section";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  OutlinedInput,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { DesktopDatePicker, LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import ShareUserAccessBox from "../../Share/ShareUserAccessBox/share-user-access-box";
import moment from "moment";
import { cacheService, shareService } from "../../../Core/Api/services";
import { UpdateShareRequest } from "../../../Core/Api/Types/update-share-request";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { UpdateShareReceipientsRequest } from "../../../Core/Api/Types/update-share-receipients-request";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { WatermarkTemplates } from "../../../Core/Api/Types/watermark-templates";
import { GetFormIsValid, GetSectionIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import { useAppSelector } from "../../../Redux/redux-store";
import FormInputText from "../../Forms/FormInputText/form-input-text";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { useTranslation } from "react-i18next";
import { minutesToTimeSpan, timeSpanToMinutes } from "../../../Utils/helpers";

export type ModalShareUpdateElementProps = {
  share: DbShareMediaInfos;
  onUpdated: (share: DbShareMediaInfos) => void;
};

export type ModalShareUpdateProps = CommonModalProps<ModalShareUpdateElementProps>;

const ModalShareUpdate = forwardRef<any, ModalShareUpdateProps>((props, ref) => {
  const { t } = useTranslation();
  const { closeModal, onUpdated, share } = props;
  const customSnackbar = useCustomSnackbar();
  const [emails, setEmails] = useState<string[]>(share.recipientEmails ?? []);
  const [showIpAndClipLimits, setShow] = useState<boolean>(false);
  const [maxViews, setMaxViews] = useState<number>(share.viewsMax);
  const [ipLim, setIpLim] = useState<number | null>(null);
  const [clipNum, setClipNum] = useState<number | null>(null);
  const [clipDur, setClipDur] = useState<string | null>(null);
  const [watermarkTpl, setWatermarkTpl] = useState<WatermarkTemplates | null>(share.watermarkTemplate);
  const [watermarkText, setWatermarkText] = useState<string | null>(share.watermarkText);
  const [endDate, setEndDate] = useState<Date>(share.endDate!);

  const formId = `form-update-share-${share.id}`;
  const formValid = useAppSelector(GetFormIsValid(formId));
  const formSectionSecurity = useAppSelector(GetSectionIsValid(formId, "sectionSecurity"));

  const hlsChunkerEnabled = cacheService.GetPlatformSettings()?.wowzaUseHlsChunker;

  const handleCancelClick = () => {
    closeModal();
  };

  const addEndDays = useCallback(
    (days: number) => {
      setEndDate(moment(endDate).add(days, "days").toDate());
    },
    [endDate]
  );

  const addViews = useCallback(
    (views: number) => {
      setMaxViews(maxViews + views);
    },
    [maxViews]
  );

  const equals = (a, b) => JSON.stringify(a) === JSON.stringify(b);

  const daysDiff: number = useMemo(() => {
    let dateA = moment(endDate);
    let dateB = moment(share.endDate!);
    let diff = dateA.diff(dateB, "days");
    return diff;
  }, [share.endDate, endDate]);

  const daysLeft: number = useMemo(() => {
    let dateA = moment();
    let dateB = moment(endDate);
    let diff = dateB.diff(dateA, "days");
    return diff;
  }, [endDate]);

  const viewsLeft: number = useMemo(() => {
    let viewed = share.viewsMax - share.viewsUntilExpire;
    let diff = maxViews - viewed;
    return diff;
  }, [share, maxViews]);

  const onSubmit = useCallback(() => {
    // Global update call
    let request = new UpdateShareRequest({
      shareId: share.id,
      maxDays: daysDiff,
      maxViews: maxViews - share.viewsMax,
      maxIPs: ipLim ? ipLim - (share.ipsMax ?? 0) : null,
      maxClipsNumber: clipNum ? clipNum - (share.clipsMaxNumber ?? 0) : null,
      maxClipsDuration: clipDur,
      watermarkTemplate: hlsChunkerEnabled ? watermarkTpl : null,
      watermarkText: hlsChunkerEnabled ? watermarkText : null,
    });

    shareService.UpdateShare(request).then((response1) => {
      if (response1.success) customSnackbar(t("alert:success.sharePropertiesUpdated"), "success");
      else {
        customSnackbar(t("alert:error.oopsWithMessage", { message: response1.message }), "warning");
        return;
      }

      // Recipient update call
      if (!equals(share.recipientEmails, emails)) {
        let request2 = new UpdateShareReceipientsRequest({
          shareId: share.id,
          emails: emails,
        });

        shareService.UpdateShareRecipients(request2).then((response2) => {
          if (response2.success) {
            customSnackbar(t("alert:success.shareRecipientsUpdated"), "success");
            props?.onUpdated(response2.share!);
            closeModal();
          } else customSnackbar(t("alert:error.oopsWithMessage", { message: response2.message }), "warning");
        });
      } else {
        props?.onUpdated(response1.share!);
        closeModal();
      }
    });
  }, [
    share,
    daysDiff,
    emails,
    endDate,
    clipNum,
    clipDur,
    ipLim,
    hlsChunkerEnabled,
    watermarkText,
    watermarkTpl,
    maxViews,
  ]);

  return (
    <Modal
      title={t("modal:title.editShareWithTitle", { titleName: share.titleName })}
      reference={ref}
      closeModal={handleCancelClick}
      width={700}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button variant="contained" color="primary" onClick={onSubmit} disabled={!formValid || emails.length === 0}>
            {t("common:submit")}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2}>
        {/* Recipients Form */}
        <ShareUserAccessBox
          share={share}
          onChange={(emails) => {
            if (emails) setEmails(emails);
          }}
        />

        {/* Accessibility Form */}
        <FormSection icon={<AccessTimeIcon />} title={t("common:accessibility")}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.primery" mb={0.5}>
                    {t("modal:modalShareUpdate.body.endDateOfValidity")}
                  </Typography>
                  <Button variant="outlined" size="small" sx={{ p: 0.5 }} onClick={() => addEndDays(10)}>
                    {t("modal:modalShareUpdate.body.plusDays", { count: 10 })}
                  </Button>
                </Stack>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DesktopDatePicker
                    format={GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS}
                    value={moment(endDate)}
                    onChange={(newValue) => {
                      if (!newValue) return;
                      setEndDate(newValue.toDate());
                    }}
                  />
                </LocalizationProvider>
                <Typography color="text.tier" variant={"caption"}>
                  {daysLeft} {t("modal:modalShareUpdate.body.daysLeft")}
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={6}>
              <Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography color="text.primery" mb={0.5}>
                    {t("modal:modalShareUpdate.body.maxViewLimit")}
                  </Typography>
                  <Button variant="outlined" size="small" sx={{ p: 0.5 }} onClick={() => addViews(10)}>
                    {t("modal:modalShareUpdate.body.plusViews", { count: 10 })}
                  </Button>
                </Stack>
                <OutlinedInput
                  onChange={(e) => setMaxViews(Number(e.target.value))}
                  value={maxViews}
                  type="number"
                  fullWidth
                  sx={{ mt: 3 }}
                />
                {maxViews !== undefined && (
                  <Typography color="text.tier" variant={"caption"}>
                    {t("modal:modalShareUpdate.body.viewLeft", { count: viewsLeft })}
                  </Typography>
                )}
              </Stack>
            </Grid>
          </Grid>

          <FormControlLabel
            sx={{ mt: 2 }}
            control={<Switch />}
            label={
              <Typography variant="body2" color="text.secondary">
                {t("field:label.ipAndClipsLimitationsSettings")}
              </Typography>
            }
            onChange={() => setShow((prev) => !prev)}
          />

          {showIpAndClipLimits && (
            <Box p={1.5} sx={{ borderRadius: 1, border: "0.1px solid #292e2d" }}>
              <Grid container spacing={1}>
                <Grid item xs={4}>
                  <Typography color="text.primary" mb={0.5}>
                    {t("modal:modalShareUpdate.body.numberOfIpAddress")}
                  </Typography>
                  <OutlinedInput value={ipLim} onChange={(e) => setIpLim(Number(e.target.value))} type="number" />
                </Grid>
                <Grid item xs={4}>
                  <Typography color="text.primary" mb={0.5}>
                    {t("modal:modalShareUpdate.body.numberOfClipsLimitation")}
                  </Typography>
                  <OutlinedInput value={clipNum} onChange={(e) => setClipNum(Number(e.target.value))} type="number" />
                  {clipNum !== undefined && (
                    <Typography mt={-1} color="text.tier" variant={"caption"}>
                      {clipNum} {t("modal:modalShareUpdate.body.clipLeft")}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={4}>
                  <FormControl sx={{ mb: 1 }}>
                    <InputLabel shrink htmlFor="maxClipDuration">
                      {t("field:label.maxDurationOfClips")}
                    </InputLabel>
                    <OutlinedInput
                      value={timeSpanToMinutes(clipDur)}
                      sx={{ width: "100%", mb: 2 }}
                      type={"number"}
                      inputProps={{
                        max: timeSpanToMinutes(cacheService.GetGroup()!.maxShareViewClipsDuration) ?? undefined,
                      }}
                      onChange={(e) => {
                        setClipDur(minutesToTimeSpan(e.target.value));
                      }}
                    />
                  </FormControl>
                </Grid>
              </Grid>
              <Alert severity="info">{t("alert:info.valuesWillBeAddedToExisting")}</Alert>
            </Box>
          )}
        </FormSection>

        {/*Live burn features: modify watermark settings*/}
        <FormSection
          sx={{
            display:
              hlsChunkerEnabled &&
              (!share.objectType ||
                share.objectType === ObjectType.Video ||
                share.objectType === ObjectType.ImageSequence ||
                share.objectType === ObjectType.GraphSkeleton ||
                share.objectType === ObjectType.Unknown ||
                share.objectType === ObjectType.DcpPackage) &&
              !share.linkedOrderItemId
                ? ""
                : "none",
          }}
          icon={<LockOutlinedIcon />}
          title={t("common:security")}
          status={formSectionSecurity ? "complete" : "warning"}
          statusText={formSectionSecurity ? watermarkText : t("common:configurationRequired")}
        >
          <FormControl sx={{ mb: 1 }}>
            <InputLabel htmlFor="watermarkTemplate" sx={{ mb: 1 }}>
              {t("field:label.watermarkTemplate")}
            </InputLabel>
            <Autocomplete
              id="watermarkTemplate"
              options={Object.values(WatermarkTemplates)}
              onChange={(e, value) => setWatermarkTpl(value ? WatermarkTemplates[value] : null)}
              popupIcon={<ExpandMoreRoundedIcon />}
              getOptionLabel={(value: string) => value || ""}
              value={watermarkTpl}
              renderInput={(params) => (
                <OutlinedInput
                  {...params.InputProps}
                  inputProps={params.inputProps}
                  placeholder={t("field:placeholder.positionOfTextOverlay")}
                  required
                  sx={{ width: "100%" }}
                />
              )}
            />
          </FormControl>
          <FormControl sx={{ mb: 1 }}>
            <InputLabel shrink htmlFor="watermarkContent" sx={{ mb: 1 }}>
              {t("common:watermarkText")}
            </InputLabel>
            <FormInputText
              label={t("common:watermarkText")}
              name="text"
              placeholder={t("field:placeholder.propertyOf")}
              value={watermarkText || ""}
              onChange={(e) => setWatermarkText(e.target.value)}
              validationProps={{
                formId: formId,
                formSectionId: "sectionSecurity",
                fieldId: "text",
                initialState: false,
                required: hlsChunkerEnabled ? true : false,
                minLength: GlobalConst.SHARE_OVERLAYTEXT_MIN_CHARS,
              }}
            />
          </FormControl>
        </FormSection>
      </Stack>
    </Modal>
  );
});

export default ModalShareUpdate;
